/* shop-finder.js — fetches a merchant-uploaded CSV of shop locations, parses it,
   and renders a searchable list + Leaflet map. One instance of this script runs
   per shop-finder section on the page (each section has its own config/DOM ids). */
(function () {
  // Excel exports CSVs with a semicolon delimiter instead of a comma in most
  // European locales (French/German/Swiss included) — sniffing the first line
  // for whichever character actually appears avoids silently failing to parse
  // a semicolon-delimited file as if it only had one giant column.
  function detectDelimiter(text) {
    const firstLine = text.split(/\r\n|\r|\n/, 1)[0] || '';
    const commas = (firstLine.match(/,/g) || []).length;
    const semicolons = (firstLine.match(/;/g) || []).length;
    return semicolons > commas ? ';' : ',';
  }

  function parseCSV(text, delimiter) {
    delimiter = delimiter || ',';
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += c;
        }
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === delimiter) {
        row.push(field);
        field = '';
      } else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(field);
        if (row.length > 1 || row[0] !== '') rows.push(row);
        row = [];
        field = '';
      } else {
        field += c;
      }
    }
    if (field !== '' || row.length) {
      row.push(field);
      rows.push(row);
    }
    return rows;
  }

  const HEADER_ALIASES = {
    name: ['name', 'shop', 'shop name', 'store', 'store name', 'dealer'],
    address: ['address', 'adress', 'adresse', 'street', 'street address'],
    city: ['city', 'town'],
    postal: ['postal_code', 'postal code', 'zip', 'zip code', 'postcode'],
    country: ['country'],
    phone: ['phone', 'tel', 'telephone', 'phone number'],
    website: ['website', 'url', 'site', 'web'],
    lat: ['lat', 'latitude'],
    lon: ['lon', 'lng', 'long', 'longitude']
  };

  function buildColumnMap(headerRow) {
    const normalized = headerRow.map(function (h) { return h.trim().toLowerCase(); });
    const map = {};
    Object.keys(HEADER_ALIASES).forEach(function (key) {
      const aliases = HEADER_ALIASES[key];
      for (let i = 0; i < normalized.length; i++) {
        if (aliases.indexOf(normalized[i]) !== -1) {
          map[key] = i;
          break;
        }
      }
    });
    return map;
  }

  function rowsToShops(rows) {
    if (rows.length < 2) return [];
    const colMap = buildColumnMap(rows[0]);
    const shops = [];

    for (let i = 1; i < rows.length; i++) {
      const r = rows[i];
      if (!r || r.every(function (v) { return v.trim() === ''; })) continue;

      const get = function (key) {
        return colMap[key] != null && r[colMap[key]] != null ? r[colMap[key]].trim() : '';
      };

      const name = get('name');
      if (!name) continue;

      const lat = parseFloat(get('lat'));
      const lon = parseFloat(get('lon'));

      shops.push({
        name: name,
        address: get('address'),
        city: get('city'),
        postal: get('postal'),
        country: get('country'),
        phone: get('phone'),
        website: get('website'),
        lat: isNaN(lat) ? null : lat,
        lon: isNaN(lon) ? null : lon
      });
    }
    return shops;
  }

  // The apple from the site logo, used as the map pin instead of a generic dot
  // or Leaflet's default teardrop marker.
  const APPLE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10.662 1.843C11.5.994 12.582.556 13.336.406a.5.5 0 0 1 .589.397c.343 1.797-.22 3.099-1.099 3.97-.853.847-1.973 1.26-2.752 1.39a.5.5 0 0 1-.576-.416c-.276-1.752.305-3.035 1.164-3.904m-.23 3.208c.552-.172 1.188-.49 1.69-.988.564-.56.98-1.367.893-2.533a4.36 4.36 0 0 0-1.642 1.016c-.563.57-.985 1.374-.94 2.505"/><path d="M6.745 1.364a.5.5 0 0 1 .618-.343c1.068.307 3.061 1.669 3.137 4.663a.5.5 0 1 1-1 .025C9.437 3.226 7.811 2.19 7.087 1.983a.5.5 0 0 1-.342-.619"/><path d="M5.798 5.516c-.715.287-1.394.82-1.883 1.434-.9 1.13-1.379 2.53-1.379 4.249 0 3.129 1.845 5.768 4.544 6.885.331.137.679.126 1.143.05q.15-.024.32-.056c.384-.07.842-.155 1.314-.155s.954.084 1.369.157q.198.036.373.062c.52.08.918.09 1.273-.058a7.42 7.42 0 0 0 4.592-6.873c-.076-1.458-.297-2.505-.721-3.322-.415-.8-1.047-1.42-2.026-1.988-.795-.33-1.359-.45-1.776-.462a1.93 1.93 0 0 0-.96.2 4 4 0 0 0-.401.23l-.069.044a8 8 0 0 1-.383.231c-.34.188-.74.345-1.271.345-.63 0-1.017-.19-1.335-.44a6 6 0 0 1-.258-.218q-.065-.057-.11-.094a1.7 1.7 0 0 0-.334-.23c-.605-.306-1.31-.277-2.022.01m-.373-.928c.896-.36 1.915-.445 2.847.028.218.11.386.238.526.355.071.06.123.105.168.146.058.05.105.093.172.145.163.128.338.227.719.227.31 0 .544-.086.788-.22a7 7 0 0 0 .403-.246c.146-.091.31-.192.495-.282a2.9 2.9 0 0 1 1.43-.302c.583.019 1.28.183 2.16.552l.028.012.027.015c1.118.643 1.915 1.395 2.442 2.41.52 1.003.755 2.218.833 3.745v.026a8.42 8.42 0 0 1-5.207 7.808c-.622.259-1.254.208-1.807.124a22 22 0 0 1-.467-.078c-.39-.068-.746-.13-1.125-.13-.372 0-.705.061-1.07.128q-.194.036-.404.071c-.508.082-1.087.133-1.685-.114-3.07-1.27-5.162-4.273-5.162-7.81 0-1.918.54-3.545 1.597-4.872.584-.733 1.398-1.379 2.292-1.738"/></svg>';

  function createPinIcon(pinIconUrl) {
    const inner = pinIconUrl
      ? '<img src="' + pinIconUrl + '" alt="">'
      : APPLE_SVG;
    const pinClass = pinIconUrl ? 'shopfinder-pin has-custom-image' : 'shopfinder-pin';
    return L.divIcon({
      className: 'shopfinder-pin-wrapper',
      html: '<div class="' + pinClass + '">' + inner + '</div>',
      iconSize: [34, 34],
      iconAnchor: [17, 17],
      tooltipAnchor: [17, 0]
    });
  }

  function normalizeWebsite(url) {
    if (!url) return '';
    if (/^https?:\/\//i.test(url)) return url;
    return 'https://' + url;
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function buildPopupHtml(shop) {
    const addressLine = [shop.address, [shop.postal, shop.city].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ');
    let html = '<div class="shopfinder-popup">';
    html += '<p class="shopfinder-popup__name">' + escapeHtml(shop.name) + '</p>';
    if (addressLine) html += '<p class="shopfinder-popup__line">' + escapeHtml(addressLine) + '</p>';
    if (shop.website) {
      html +=
        '<a class="shopfinder-popup__link" href="' +
        escapeHtml(normalizeWebsite(shop.website)) +
        '" target="_blank" rel="noopener">Website</a>';
    }
    html += '</div>';
    return html;
  }

  function haversineDistanceKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  /* Geocoding: turns "address + postal code (+ city/country)" into a pin position
     for any shop whose CSV row doesn't already have lat/lon. Uses OpenStreetMap's
     free Nominatim API — no key needed, but its usage policy caps requests at
     roughly 1/second, so lookups run one at a time with a delay rather than all
     at once. Results are cached in localStorage (addresses essentially never
     move), so a given visitor's browser only ever geocodes each address once —
     every later page view for them resolves instantly from the cache. CSV-
     provided lat/lon always wins when present; this only fills in the gaps. */
  const GEOCODE_CACHE_KEY = 'shopfinder_geocode_cache_v1';
  const GEOCODE_DELAY_MS = 1100;

  function loadGeocodeCache() {
    try {
      return JSON.parse(window.localStorage.getItem(GEOCODE_CACHE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function saveGeocodeCache(cache) {
    try {
      window.localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      /* localStorage unavailable (private browsing, quota) — geocoding still
         works for this page view, it just won't be cached for next time. */
    }
  }

  function geocodeAddress(query) {
    const url = 'https://nominatim.openstreetmap.org/search?format=json&limit=1&q=' + encodeURIComponent(query);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : []; })
      .then(function (results) {
        if (results && results[0]) {
          return { lat: parseFloat(results[0].lat), lon: parseFloat(results[0].lon) };
        }
        return null;
      })
      .catch(function () { return null; });
  }

  function geocodeMissingShops(shops, defaultCountry, onResolved) {
    const cache = loadGeocodeCache();
    const queue = shops.filter(function (shop) {
      return (shop.lat == null || shop.lon == null) && (shop.address || shop.postal);
    });

    function next(i) {
      if (i >= queue.length) return;
      const shop = queue[i];
      const query = [shop.address, shop.postal, shop.city, shop.country || defaultCountry]
        .filter(Boolean)
        .join(', ');
      const cacheKey = query.toLowerCase();

      if (cache[cacheKey]) {
        shop.lat = cache[cacheKey].lat;
        shop.lon = cache[cacheKey].lon;
        onResolved(shop);
        next(i + 1);
        return;
      }

      geocodeAddress(query).then(function (coords) {
        if (coords) {
          shop.lat = coords.lat;
          shop.lon = coords.lon;
          cache[cacheKey] = coords;
          saveGeocodeCache(cache);
          onResolved(shop);
        }
        setTimeout(function () { next(i + 1); }, GEOCODE_DELAY_MS);
      });
    }

    next(0);
  }

  function initShopFinder(section) {
    const sectionId = section.getAttribute('data-section-id');
    const configEl = document.getElementById('ShopFinderConfig-' + sectionId);
    const listEl = document.getElementById('ShopFinderList-' + sectionId);
    const countEl = document.getElementById('ShopFinderCount-' + sectionId);
    const searchEl = document.getElementById('ShopFinderSearch-' + sectionId);
    const mapEl = document.getElementById('ShopFinderMap-' + sectionId);
    if (!configEl || !listEl) return;

    let config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }

    if (!config.csvUrl) return;

    let map = null;
    const markers = {};

    function ensureMap() {
      if (map || typeof L === 'undefined' || !mapEl) return;
      map = L.map(mapEl).setView([config.centerLat, config.centerLng], config.zoom);
      // CARTO's keyless basemap tiles (tried here first for a true "no labels"
      // look) now require a paid API key — every tile just returns an "API KEY
      // REQUIRED" watermark. Falling back to standard OpenStreetMap tiles (free,
      // no key, same source already used on the Factories page map), with the
      // grayscale/contrast filter below doing the minimal-look work instead.
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
    }

    function setActive(index) {
      listEl.querySelectorAll('.shopfinder-item.is-active').forEach(function (el) {
        el.classList.remove('is-active');
      });
      const item = listEl.querySelector('.shopfinder-item[data-index="' + index + '"]');
      if (item) item.classList.add('is-active');

      Object.keys(markers).forEach(function (key) {
        const el = markers[key].getElement();
        if (!el) return;
        const pin = el.querySelector('.shopfinder-pin');
        if (pin) pin.classList.toggle('is-active', key === String(index));
      });
    }

    function renderList(shops, radiusMode) {
      listEl.innerHTML = '';

      if (shops.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'shopfinder-empty';
        empty.textContent = radiusMode
          ? 'No shops within ' + config.searchRadiusKm + ' km of that location.'
          : 'No shops match your search.';
        listEl.appendChild(empty);
        if (countEl) countEl.textContent = '';
        return;
      }

      if (countEl) {
        countEl.textContent = radiusMode
          ? shops.length + (shops.length === 1 ? ' shop' : ' shops') + ' within ' + config.searchRadiusKm + ' km'
          : shops.length + (shops.length === 1 ? ' shop' : ' shops');
      }

      shops.forEach(function (shop, index) {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'shopfinder-item';
        item.dataset.index = String(index);

        const nameRow = document.createElement('div');
        nameRow.className = 'shopfinder-item__name-row';

        const nameEl = document.createElement('p');
        nameEl.className = 'shopfinder-item__name';
        nameEl.textContent = shop.name;
        nameRow.appendChild(nameEl);

        if (shop.distanceKm != null) {
          const distEl = document.createElement('span');
          distEl.className = 'shopfinder-item__distance';
          distEl.textContent = shop.distanceKm < 1
            ? Math.round(shop.distanceKm * 1000) + ' m'
            : shop.distanceKm.toFixed(1) + ' km';
          nameRow.appendChild(distEl);
        }

        item.appendChild(nameRow);

        const addressLine = [shop.address, [shop.postal, shop.city].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', ');
        if (addressLine) {
          const addrEl = document.createElement('span');
          addrEl.className = 'shopfinder-item__line';
          addrEl.textContent = addressLine;
          item.appendChild(addrEl);
        }

        if (shop.phone) {
          const phoneEl = document.createElement('span');
          phoneEl.className = 'shopfinder-item__line';
          phoneEl.textContent = shop.phone;
          item.appendChild(phoneEl);
        }

        if (shop.website) {
          const links = document.createElement('span');
          links.className = 'shopfinder-item__links';
          if (shop.website) {
            const a = document.createElement('a');
            a.href = normalizeWebsite(shop.website);
            a.target = '_blank';
            a.rel = 'noopener';
            a.textContent = 'Website';
            a.addEventListener('click', function (e) { e.stopPropagation(); });
            links.appendChild(a);
          }
          item.appendChild(links);
        }

        item.addEventListener('click', function () {
          setActive(index);

          if (shop.lat != null && shop.lon != null && map) {
            map.setView([shop.lat, shop.lon], Math.max(config.zoom, 12), { animate: true });
            const marker = markers[index];
            if (marker) marker.openTooltip();
          }
        });

        listEl.appendChild(item);
      });
    }

    function renderMarkers(shops) {
      if (!map) return;
      Object.keys(markers).forEach(function (key) { map.removeLayer(markers[key]); });
      for (const key in markers) delete markers[key];

      const bounds = [];
      shops.forEach(function (shop, index) {
        if (shop.lat == null || shop.lon == null) return;
        const marker = L.marker([shop.lat, shop.lon], { icon: createPinIcon(config.pinIconUrl) }).addTo(map);
        marker.bindTooltip(shop.name, {
          direction: 'right',
          offset: [16, 0],
          className: 'shopfinder-map__label'
        });
        // Full shop details (address, website) — opens automatically on click,
        // since bindPopup wires that up on the marker by itself.
        marker.bindPopup(buildPopupHtml(shop), {
          className: 'shopfinder-popup-wrapper',
          offset: [0, -8],
          closeButton: true
        });
        marker.on('click', function () {
          const item = listEl.querySelector('.shopfinder-item[data-index="' + index + '"]');
          if (item) {
            item.click();
            item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else {
            setActive(index);
          }
        });
        markers[index] = marker;
        bounds.push([shop.lat, shop.lon]);
      });

      if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [30, 30] });
      } else if (bounds.length === 1) {
        map.setView(bounds[0], Math.max(config.zoom, 11));
      }
    }

    let allShops = [];
    let currentTerm = '';

    // Radius search: once the typed search term itself has been geocoded to a
    // location (see tryResolveSearchLocation below), shops within
    // config.searchRadiusKm of that point are shown, nearest first, instead of
    // plain text matching. searchLocationTerm records exactly which term that
    // location belongs to, so if the visitor keeps typing past it, filtering
    // falls back to substring matching for the new term until IT resolves too.
    let searchLocation = null;
    let searchLocationTerm = '';
    let geocodeDebounceTimer = null;

    function getFiltered(term) {
      const q = term.trim().toLowerCase();
      if (!q) return allShops;

      if (searchLocation && q === searchLocationTerm) {
        return allShops
          .filter(function (shop) { return shop.lat != null && shop.lon != null; })
          .map(function (shop) {
            shop.distanceKm = haversineDistanceKm(searchLocation.lat, searchLocation.lon, shop.lat, shop.lon);
            return shop;
          })
          .filter(function (shop) { return shop.distanceKm <= config.searchRadiusKm; })
          .sort(function (a, b) { return a.distanceKm - b.distanceKm; });
      }

      return allShops.filter(function (shop) {
        return (
          shop.name.toLowerCase().indexOf(q) !== -1 ||
          shop.city.toLowerCase().indexOf(q) !== -1 ||
          shop.postal.toLowerCase().indexOf(q) !== -1 ||
          shop.address.toLowerCase().indexOf(q) !== -1
        );
      });
    }

    function isRadiusSearch(term) {
      return !!(searchLocation && term.trim().toLowerCase() === searchLocationTerm);
    }

    function applyFilter(term) {
      currentTerm = term;
      const filtered = getFiltered(term);
      renderList(filtered, isRadiusSearch(term));
      renderMarkers(filtered);
    }

    // Lighter refresh used while background geocoding resolves addresses one at
    // a time: only updates the map pins, leaving the already-rendered list (and
    // its scroll position / active row) untouched.
    function refreshMarkersOnly() {
      renderMarkers(getFiltered(currentTerm));
    }

    // Tries to resolve the search box's own text as a location (a city, postal
    // code, or address the visitor typed) so getFiltered can switch into radius
    // mode. Runs after a pause in typing rather than per-keystroke, both to stay
    // within Nominatim's rate limit and because there's no point geocoding text
    // the visitor hasn't finished typing yet.
    function tryResolveSearchLocation(term) {
      const q = term.trim();
      if (q.length < 3 || q.toLowerCase() === searchLocationTerm) return;

      const cache = loadGeocodeCache();
      const cacheKey = q.toLowerCase();
      if (cache[cacheKey]) {
        searchLocation = cache[cacheKey];
        searchLocationTerm = cacheKey;
        applyFilter(searchEl.value);
        return;
      }

      geocodeAddress(q + ', ' + config.defaultCountry).then(function (coords) {
        if (!coords) return;
        // The visitor may have kept typing while this was in flight — only
        // apply the result if the box still holds the exact text we geocoded.
        if (!searchEl || searchEl.value.trim().toLowerCase() !== cacheKey) return;
        cache[cacheKey] = coords;
        saveGeocodeCache(cache);
        searchLocation = coords;
        searchLocationTerm = cacheKey;
        applyFilter(searchEl.value);
      });
    }

    if (searchEl) {
      searchEl.addEventListener('input', function () {
        applyFilter(searchEl.value);
        clearTimeout(geocodeDebounceTimer);
        geocodeDebounceTimer = setTimeout(function () {
          tryResolveSearchLocation(searchEl.value);
        }, 700);
      });
    }

    ensureMap();

    // A build-time cache (assets/shopfinder-geocode-cache.json) shipped with the
    // theme, pre-resolved for every address in the CSV at the time it was last
    // regenerated. Applying it before the first render means shops appear on
    // the map immediately instead of trickling in one-by-one from live
    // Nominatim lookups (rate-limited to ~1/second). Only shops missing from
    // this file \u2014 added to the CSV since it was last generated \u2014 still fall
    // back to live geocoding.
    const staticCachePromise = config.staticGeocodeUrl
      ? fetch(config.staticGeocodeUrl).then(function (res) { return res.ok ? res.json() : {}; }).catch(function () { return {}; })
      : Promise.resolve({});

    Promise.all([
      fetch(config.csvUrl).then(function (res) {
        if (!res.ok) throw new Error('CSV fetch failed: ' + res.status);
        return res.text();
      }),
      staticCachePromise
    ])
      .then(function (results) {
        const text = results[0];
        const staticCache = results[1] || {};
        const cleanText = text.replace(/^\uFEFF/, '');
        allShops = rowsToShops(parseCSV(cleanText, detectDelimiter(cleanText)));

        allShops.forEach(function (shop) {
          if (shop.lat != null && shop.lon != null) return;
          const query = [shop.address, shop.postal, shop.city, shop.country || config.defaultCountry]
            .filter(Boolean)
            .join(', ');
          const hit = staticCache[query.toLowerCase()];
          if (hit) {
            shop.lat = hit.lat;
            shop.lon = hit.lon;
          }
        });

        applyFilter(searchEl ? searchEl.value : '');

        geocodeMissingShops(allShops, config.defaultCountry, refreshMarkersOnly);
      })
      .catch(function () {
        listEl.innerHTML = '<p class="shopfinder-empty">Couldn\'t load the shop list. Check that the CSV file URL in the section settings is correct and publicly accessible.</p>';
      });
  }

  function init() {
    document.querySelectorAll('[data-shop-finder]').forEach(initShopFinder);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      const el = e.target.querySelector('[data-shop-finder]');
      if (el) initShopFinder(el);
    });
  }
})();
